import { Component, input, output, signal, computed, effect, inject } from '@angular/core';
import { UiService } from '../../../services/ui.service';
import { I18nService } from '../../../services/i18n.service';
import { Location } from '../../../models/classes/location';
import { ValidationInputComponent } from '../../validation-input/validation-input';
import { NavButtonComponent } from '../../nav-button/nav-button';
import { typeOfDeleteObj } from '../../../utilities/global.util';
import { GLOBAL_CONSTANT } from '../../../constants/global.constant';
import { I18N_KEYS } from '../../../enums/i18.enum';
import { Plant_Structure_Enums } from '../../../enums/global.enum';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { DeletePopupComponent } from '../../delete-popup/delete-popup';

@Component({
  selector: 'app-rename-card-header',
  templateUrl: './rename-card-header.html',
  styleUrls: ['./rename-card-header.scss'],
  imports: [ValidationInputComponent, NavButtonComponent, NzIconModule, DeletePopupComponent],
})
export class RenameCardHeaderComponent {
  typeOfDeleteObj = typeOfDeleteObj;

  // Inputs
  location = input<Location | null>(null);
  parentArray = input<Location[]>([]);
  isEditable = input<boolean>(false);
  editObject = input<any>(null);
  innerWrapper = input<boolean>(false);
  sidebar = input<boolean>(false);

  // Outputs
  delete = output<Location>();
  nameError = output<boolean>();
  editCompleted = output<void>();

  public i18n = inject(I18nService);
  private ui = inject(UiService);

  editedObjectId = signal<number>(GLOBAL_CONSTANT.DEFAULT_INITIALIZER);
  showRename = signal<boolean>(false);
  allowEmptySave = signal<boolean>(false);
  overlayDeletePopup = signal<boolean>(false);
  overlayDeleteObject = signal<Location | any>(null);
  nameErrorFlag = signal<boolean>(false);
  nameDuplicate = signal<boolean>(false);

  // ✅ keep editable value here
  editedName = signal<string>(GLOBAL_CONSTANT.EMPTY_STRING);

  isMobile = computed(() => this.ui.IsMobile());

  isInEditingMode = computed(
    () =>
      this.showRename() && this.isEditable() && this.editedObjectId() === this.location()?.objectID
  );

  isDisabled = computed(
    () =>
      this.isEditable() &&
      this.editedObjectId() !== GLOBAL_CONSTANT.DEFAULT_INITIALIZER &&
      this.editedObjectId() !== this.location()?.objectID
  );

  constructor() {
    effect(() => {
      const objectInEditingId = this.ui.ObjectInEditing() ?? GLOBAL_CONSTANT.DEFAULT_INITIALIZER;
      this.editedObjectId.set(objectInEditingId);
    });
  }

  getRenamePlaceholder(defaultString: string): string {
    return defaultString;
  }

  getRenameLabel(): string {
    const loc = this.location();
    if (!loc) return GLOBAL_CONSTANT.EMPTY_STRING;

    return loc.curLayer === Plant_Structure_Enums.LAYER_D
      ? this.i18n.langString(
          loc.IsBuilding ? I18N_KEYS.ASSET_LAYER_BUILDING_LABEL : I18N_KEYS.ASSET_LAYER_AREA_LABEL
        )
      : this.i18n.langString(I18N_KEYS.ASSET_LAYER_LABEL + loc.curLayer);
  }

  getSiblings(): Location[] {
    const loc = this.location();
    if (!loc) return [];

    const currentName = this.editedName()?.trim().toLowerCase();

    return this.parentArray().filter((item) => {
      // ✅ exclude self
      if (item.objectID === loc.objectID) return false;

      // ✅ VERY IMPORTANT: ignore same-name (original case)
      return item.objectName?.trim().toLowerCase() !== currentName;
    });
  }

  onNameErrorChange(event: boolean) {
    this.nameErrorFlag.set(event);
    this.nameError.emit(event);
  }

  onNameDuplicate(event: boolean) {
    this.nameDuplicate.set(event);
  }

  onNameChange(value: string) {
    const newValue = value ?? GLOBAL_CONSTANT.EMPTY_STRING;
    this.editedName.set(newValue);

    const loc = this.location();
    if (loc) {
      // ✅ update real object also, so parent/page buttons get enabled
      loc.objectName = newValue;
    }
  }

  allowContinue = computed(() => {
    return !!this.editedName()?.trim() && !this.nameErrorFlag() && !this.nameDuplicate();
  });

  onStartEdit() {
    if (this.isDisabled()) return;

    const loc = this.location();
    if (!loc) return;

    // ✅ Reset everything cleanly
    this.showRename.set(true);
    this.nameErrorFlag.set(false);
    this.nameDuplicate.set(false);

    this.editedName.set(loc.objectName ?? '');

    // ✅ Ensure no stale global state
    this.ui.updateObjectInEditing(GLOBAL_CONSTANT.DEFAULT_INITIALIZER);

    setTimeout(() => {
      this.ui.updateObjectInEditing(loc.objectID);
    });
  }

  onStopEdit() {
    const loc = this.location();
    if (!loc) return;

    // ✅ Save value
    loc.objectName = this.editedName().trim();

    this.showRename.set(false);

    // ✅ HARD RESET (very important)
    this.nameErrorFlag.set(false);
    this.nameDuplicate.set(false);

    // ✅ FORCE UiService reset
    this.ui.updateObjectInEditing(GLOBAL_CONSTANT.DEFAULT_INITIALIZER);

    this.editCompleted.emit();
  }

  onDeletePopup(locationToDelete: Location) {
    console.log('locationToDelete: ', locationToDelete);
    this.overlayDeletePopup.set(true);
    this.overlayDeleteObject.set(locationToDelete);
  }

  onExit(nav: boolean) {
    this.overlayDeletePopup.set(false);
    if (nav && this.overlayDeleteObject()) {
      this.onDelete(this.overlayDeleteObject()!);
    }
  }

  onDelete(locationToDelete: Location) {
    this.delete.emit(locationToDelete);
    this.onStopEdit();
  }

  objectNameLabel(): string {
    const loc = this.location();
    if (!loc) return GLOBAL_CONSTANT.EMPTY_STRING;

    return loc.objectName && loc.objectName !== GLOBAL_CONSTANT.EMPTY_STRING
      ? loc.objectName
      : this.i18n.langString('default_name_' + loc.curLayer);
  }
}
